/**
 * Raised by the rights-profiles API when a DELETE is refused because the profile
 * is still assigned to users (HTTP 409). It carries what `DeleteProfileDialog`
 * needs — the assigned-user count and, when parseable, the profile name.
 *
 * The backend exposes both ONLY inside the 409 message string
 * (`Cannot delete profile "X": it is assigned to N user(s)`), with no structured
 * body fields, so we parse them here. `assignedUsersCount` is `null` when the
 * message can't be parsed; the hook then falls back to the count it already holds
 * from the list row.
 */
export class ProfileInUseError extends Error {
  readonly isProfileInUse = true;
  readonly assignedUsersCount: number | null;
  readonly profileName?: string;

  constructor(assignedUsersCount: number | null, profileName?: string) {
    super(
      profileName
        ? `Profile "${profileName}" is assigned to ${assignedUsersCount ?? 'some'} user(s)`
        : `Profile is assigned to ${assignedUsersCount ?? 'some'} user(s)`,
    );
    this.name = 'ProfileInUseError';
    this.assignedUsersCount = assignedUsersCount;
    this.profileName = profileName;
    Object.setPrototypeOf(this, ProfileInUseError.prototype);
  }

  static is(error: unknown): error is ProfileInUseError {
    return error instanceof ProfileInUseError;
  }
}

/**
 * Extracts the active-user count from the backend 409 message (`null` if absent).
 * Handles the French `ProfileInUseException` wording actually emitted
 * (`Suppression bloquée : N utilisateur(s) actif(s)…`) as well as the legacy
 * English format — otherwise the count falls back to `assigned_users_count`,
 * which also counts inactive assignments.
 */
export function parseAssignedCount(message: string | undefined): number | null {
  const match = message?.match(
    /(?:assigned to |suppression bloquée\s*:\s*)(\d+)\s+(?:user|utilisateur)/i,
  );
  return match ? Number(match[1]) : null;
}

/** Extracts the profile name from the backend 409 message (`undefined` if absent). */
export function parseProfileName(message: string | undefined): string | undefined {
  return message?.match(/profile "([^"]+)"/i)?.[1];
}
